import { useTranslation } from 'next-i18next';
import React, { ReactElement } from 'react';
import { AuthChannelType } from '@hotelplan/fdr.lib.ident.auth-config';
import { useAuthentication } from '@hotelplan/fdr.lib.ident.with-auth';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { useObjectId } from '@hotelplan/libs.context.object-id';
import {
  FdrCo2Footprint as FdrCo2FootprintType,
  FdrPdf,
  FdrProductB2bInformationGroup,
  FdrRequestButton,
} from '@hotelplan/supergraph-api';
import { ProductPinType } from 'components/domain/fdr-adventure-travel-routes';
import { FdrB2bAdditionalInfo } from 'components/domain/fdr-b2b-additional-info';
import FdrBenefitItems from 'components/domain/fdr-benefits-section/fdr-benefit-items';
import {
  BenefitContainer,
  BenefitSectionContent,
  BenefitText,
} from 'components/domain/fdr-benefits-section/fdr-benefits-section';
import {
  FdrCo2Footprint,
  getIsShowCo2Footprint,
} from 'components/domain/fdr-co2-footprint';
import { FdrGalleryWithMap } from 'components/domain/fdr-gallery-with-map';
import {
  ClickEventSourceType,
  trackRequestFormClick,
} from 'components/domain/tracking/tracking.utils';
import {
  AdventureTravelGalleryWithMapFragment,
  useFdrAdventureTravelGalleryWithMapQuery,
} from 'schemas/query/adventure-travel/fdr-adventure-travel-gallery-with-map.generated';
import {
  FdrIndividualOfferButtonRequestButton,
  FdrRequestButtonContainer,
} from './fdr-pdp.styles';

interface IFdrAdventureTravelContentProps {
  co2Footprint: FdrCo2FootprintType;
  b2bInfoGroups: Array<FdrProductB2bInformationGroup>;
  hotelPdfs?: Array<FdrPdf>;
  staticRequestButton: FdrRequestButton;
}

export function FdrAdventureTravelContent({
  staticRequestButton,
  co2Footprint,
  b2bInfoGroups,
  hotelPdfs,
}: IFdrAdventureTravelContentProps): ReactElement {
  const { id } = useObjectId();
  const { channelType } = useAuthentication();

  const isShowCo2Footprint = getIsShowCo2Footprint(co2Footprint);

  const { data, loading } = useFdrAdventureTravelGalleryWithMapQuery({
    variables: { id },
  });

  const product = data?.fdrProduct as AdventureTravelGalleryWithMapFragment;

  return (
    <>
      <FdrGalleryWithMap
        name={product?.name}
        routes={product?.route}
        images={product?.images}
        defaultPinType={ProductPinType.RouteIcon}
        loading={loading}
      />
      {isShowCo2Footprint ? (
        <FdrCo2Footprint
          {...co2Footprint}
          requestButton={staticRequestButton}
        />
      ) : null}
      {channelType !== AuthChannelType.B2B ? (
        <FdrIndividualOffer staticRequestButton={staticRequestButton} />
      ) : null}
      {b2bInfoGroups?.length ? (
        <FdrB2bAdditionalInfo
          hotelPdfs={hotelPdfs}
          b2bInfoGroups={b2bInfoGroups}
        />
      ) : null}
    </>
  );
}

interface IFdrIndividualOfferProps {
  staticRequestButton: FdrRequestButton;
}

function FdrIndividualOffer({
  staticRequestButton,
}: IFdrIndividualOfferProps): ReactElement {
  const [t] = useTranslation('pdp');
  const { mobile } = useDeviceType();

  const items = [`box1`, `box2`, `box3`, `box4`].map(k => ({
    text: t(`pdp.individualOffer.usp.${k}`),
  }));

  return (
    <BenefitSectionContent>
      <BenefitContainer>
        <BenefitText>
          <h2>{t('pdp.individualOffer.title')}</h2>
          <div>{t('pdp.individualOffer.intro')}</div>
        </BenefitText>
        <FdrBenefitItems items={items} />
        {staticRequestButton ? (
          <FdrRequestButtonContainer mobile={mobile}>
            <FdrIndividualOfferButtonRequestButton
              link={staticRequestButton.link}
              caption={staticRequestButton.objectCaption}
              backgroundColor="paperGrey"
              onClick={() => {
                trackRequestFormClick(ClickEventSourceType.USP);
              }}
            />
          </FdrRequestButtonContainer>
        ) : null}
      </BenefitContainer>
    </BenefitSectionContent>
  );
}
